import React from 'react';
import { motion } from 'framer-motion';

/**
 * ActiveSessionView - "FaceTime with Mira" Experience
 *
 * The core visual layer during voice sessions. Replaces blank screen with
 * an engaging, social-first interface for 6-12 grade students.
 *
 * Design Principles (per UX Guide + CashApp principle):
 * - Simplicity removes uncertainty - orb glowing = AI active
 * - 2025 design language: Siri orb, Google dots, Discord rings
 * - Social features for logged-in users (Discord-style)
 * - Guests see just the orb (clean, focused)
 */

// Custom Mira orb color options (purchasable in shop)
const MIRA_CUSTOM_COLORS = {
  default: { primary: '#297A92', secondary: '#4d99a5', accent: '#2DD4BF' },
  coral: { primary: '#FF6B6B', secondary: '#FF8E8E', accent: '#FF5252' },
  ocean: { primary: '#4ECDC4', secondary: '#6EE7DF', accent: '#2DD4BF' },
  sunset: { primary: '#FF9F43', secondary: '#FFB76B', accent: '#FF7F11' },
  midnight: { primary: '#5F27CD', secondary: '#7C4DFF', accent: '#A855F7' },
  forest: { primary: '#26DE81', secondary: '#4AE590', accent: '#10B981' },
  lavender: { primary: '#A55EEA', secondary: '#C084FC', accent: '#9333EA' },
};

// Helper to get state colors based on custom color choice
const getMiraStateColors = (customColor = 'default') => {
  const colors = MIRA_CUSTOM_COLORS[customColor] || MIRA_CUSTOM_COLORS.default;
  return {
    idle: { label: 'Mira', color: colors.primary, glowColor: `${colors.primary}80` },
    listening: { label: 'Listening...', color: colors.secondary, glowColor: `${colors.secondary}80` },
    thinking: { label: 'Thinking...', color: colors.secondary, glowColor: `${colors.secondary}80` },
    speaking: { label: 'Mira', color: colors.accent, glowColor: `${colors.accent}99` },
    celebrating: { label: 'Great job!', color: colors.accent, glowColor: `${colors.accent}99` }
  };
};

// Mira orb states - Tappy brand colors (trust-focused) - DEFAULT
const MIRA_STATES = {
  idle: { label: 'Mira', color: '#297A92', glowColor: 'rgba(41, 122, 146, 0.5)' },
  listening: { label: 'Listening...', color: '#4d99a5', glowColor: 'rgba(77, 153, 165, 0.5)' },
  thinking: { label: 'Thinking...', color: '#6DB4CD', glowColor: 'rgba(109, 180, 205, 0.5)' },
  speaking: { label: 'Mira', color: '#2DD4BF', glowColor: 'rgba(45, 212, 191, 0.6)' },
  celebrating: { label: 'Great job!', color: '#4ECDC4', glowColor: 'rgba(78, 205, 196, 0.6)' }
};

// Clutch Time orb states - Amber/Orange (urgency, focus, energy)
const CLUTCH_TIME_STATES = {
  idle: { label: 'Clutch Time', color: '#F59E0B', glowColor: 'rgba(245, 158, 11, 0.5)' },
  listening: { label: 'Listening...', color: '#FBBF24', glowColor: 'rgba(251, 191, 36, 0.5)' },
  thinking: { label: 'Thinking...', color: '#F59E0B', glowColor: 'rgba(245, 158, 11, 0.5)' },
  speaking: { label: 'Mira', color: '#FB923C', glowColor: 'rgba(251, 146, 60, 0.6)' },
  celebrating: { label: 'Nice!', color: '#FBBF24', glowColor: 'rgba(251, 191, 36, 0.6)' }
};

/**
 * MiraOrb - 2025-style animated orb (Siri/Google/ChatGPT inspired)
 * When isClutchTime is true, uses amber/orange color scheme
 * Supports custom color via miraOrbColor prop (purchasable in shop)
 *
 * 2026 Update: curiosityMomentum (0-1) affects orb physics continuously
 * - Higher momentum = deeper breathing, warmer glow, more responsive
 * - No explicit UI changes - students FEEL it, don't see numbers
 */
const MiraOrb = ({
  state = 'idle',
  isClutchTime = false,
  miraOrbColor = 'default',
  miraGlowEffect = 'default',
  curiosityMomentum = 0.5  // 2026: Continuous 0-1 value affecting physics
}) => {
  // Priority: Clutch Time > Custom Color > Default
  const states = isClutchTime
    ? CLUTCH_TIME_STATES
    : (miraOrbColor !== 'default' ? getMiraStateColors(miraOrbColor) : MIRA_STATES);
  const stateConfig = states[state] || states.idle;

  // 2026 Curiosity Physics: Momentum affects animation intensity
  // Higher momentum = more "alive" feeling (deeper breath, warmer glow)
  const momentumMultiplier = 0.7 + (curiosityMomentum * 0.6); // 0.7-1.3 range
  const pulseDepth = 1 + (0.02 + curiosityMomentum * 0.08); // 1.02-1.10 scale max
  const animationSpeed = 3 - (curiosityMomentum * 1.5); // 3s-1.5s (faster when curious)

  // Warmth interpolation: teal (#297A92) → warm accent (#4ECDC4 → #F472B6 at max)
  // Subtle shift - students feel warmth without explicit color change announcement
  const getWarmthAdjustedColor = (baseColor) => {
    if (curiosityMomentum < 0.6 || isClutchTime) return baseColor;
    // Only add warmth at higher momentum (0.6+), and very subtly
    const warmthAmount = (curiosityMomentum - 0.6) * 0.5; // 0-0.2 max
    // Slight pink/warm tint added via filter in CSS instead of color math
    return baseColor;
  };

  // Clutch Time uses faster, more energetic "heartbeat" animations
  // 2026: curiosityMomentum affects all variants via pulseDepth and animationSpeed
  const orbVariants = isClutchTime ? {
    idle: {
      scale: [1, 1.04 * momentumMultiplier, 1],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
    },
    listening: {
      scale: [1, 1.1 * momentumMultiplier, 1],
      transition: { duration: 1, repeat: Infinity, ease: "easeInOut" }
    },
    thinking: {
      scale: [1, 1.06 * momentumMultiplier, 1],
      rotate: [0, 180, 360],
      transition: { duration: 1.5, repeat: Infinity, ease: "linear" }
    },
    speaking: {
      scale: [1, 1.08 * momentumMultiplier, 1],
      transition: { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
    },
    celebrating: {
      scale: [1, 1.15 * momentumMultiplier, 1],
      rotate: [0, 10, -10, 0],
      transition: { duration: 0.4, repeat: Infinity, ease: "easeInOut" }
    }
  } : {
    // Standard mode: curiosityMomentum creates continuous "aliveness"
    // Low momentum (0.3): slow, calm breathing
    // High momentum (0.8+): deeper, more responsive, almost dancing
    idle: {
      scale: [1, pulseDepth, 1],
      transition: { duration: animationSpeed, repeat: Infinity, ease: "easeInOut" }
    },
    listening: {
      scale: [1, 1.04 + (curiosityMomentum * 0.06), 1], // 1.04-1.10
      transition: { duration: Math.max(1, 1.5 - curiosityMomentum * 0.5), repeat: Infinity, ease: "easeInOut" }
    },
    thinking: {
      scale: [1, 1.03 + (curiosityMomentum * 0.04), 1],
      rotate: [0, 180, 360],
      transition: { duration: 2 - (curiosityMomentum * 0.5), repeat: Infinity, ease: "linear" }
    },
    speaking: {
      scale: [1, 1.04 + (curiosityMomentum * 0.04), 1], // More expressive when curious
      transition: { duration: Math.max(0.8, 1.2 - curiosityMomentum * 0.4), repeat: Infinity, ease: "easeInOut" }
    },
    celebrating: {
      scale: [1, 1.15 * momentumMultiplier, 1],
      rotate: [0, 10, -10, 0],
      transition: { duration: 0.5, repeat: Infinity, ease: "easeInOut" }
    }
  };

  // Clutch Time glow is more intense and faster
  // 2026: curiosityMomentum adds warmth/intensity to glow at higher values
  const glowIntensityBoost = curiosityMomentum * 0.15; // 0-0.15 opacity boost
  const glowScaleBoost = curiosityMomentum * 0.1; // 0-0.1 scale boost

  const glowVariants = isClutchTime ? {
    idle: {
      scale: [1, 1.15, 1],
      opacity: [0.4, 0.6, 0.4],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
    },
    listening: {
      scale: [1.1, 1.35, 1.1],
      opacity: [0.5, 0.8, 0.5],
      transition: { duration: 1, repeat: Infinity, ease: "easeInOut" }
    },
    thinking: {
      scale: [1.1, 1.35, 1.1],
      opacity: [0.5, 0.8, 0.5],
      transition: { duration: 1, repeat: Infinity, ease: "easeInOut" }
    },
    speaking: {
      scale: [1.1, 1.3, 1.1],
      opacity: [0.6, 0.8, 0.6],
      transition: { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
    },
    celebrating: {
      scale: [1.2, 1.5, 1.2],
      opacity: [0.7, 1, 0.7],
      transition: { duration: 0.3, repeat: Infinity, ease: "easeInOut" }
    }
  } : {
    // Standard mode: glow responds to curiosityMomentum
    // Higher momentum = slightly more intense, warmer-feeling glow
    idle: {
      scale: [1, 1.1 + glowScaleBoost, 1],
      opacity: [0.3 + glowIntensityBoost, 0.5 + glowIntensityBoost, 0.3 + glowIntensityBoost],
      transition: { duration: animationSpeed, repeat: Infinity, ease: "easeInOut" }
    },
    listening: {
      scale: [1.1, 1.3 + glowScaleBoost, 1.1],
      opacity: [0.5 + glowIntensityBoost, 0.7 + glowIntensityBoost, 0.5 + glowIntensityBoost],
      transition: { duration: Math.max(1, 1.5 - curiosityMomentum * 0.5), repeat: Infinity, ease: "easeInOut" }
    },
    thinking: {
      scale: [1.1, 1.3 + glowScaleBoost, 1.1],
      opacity: [0.4 + glowIntensityBoost, 0.7 + glowIntensityBoost, 0.4 + glowIntensityBoost],
      transition: { duration: Math.max(1, 1.5 - curiosityMomentum * 0.5), repeat: Infinity, ease: "easeInOut" }
    },
    speaking: {
      scale: [1.1, 1.25 + glowScaleBoost, 1.1],
      opacity: [0.5 + glowIntensityBoost, 0.7 + glowIntensityBoost, 0.5 + glowIntensityBoost],
      transition: { duration: Math.max(0.8, 1.2 - curiosityMomentum * 0.4), repeat: Infinity, ease: "easeInOut" }
    },
    celebrating: {
      scale: [1.2, 1.5 + glowScaleBoost, 1.2],
      opacity: [0.6 + glowIntensityBoost, 1, 0.6 + glowIntensityBoost],
      transition: { duration: 0.4, repeat: Infinity, ease: "easeInOut" }
    }
  };

  const sparkleVariants = {
    idle: { opacity: 0 },
    listening: { opacity: [0, 0.8, 0], scale: [0.8, 1.2, 0.8] },
    thinking: { opacity: [0.3, 0.6, 0.3], rotate: [0, 360] },
    speaking: { opacity: [0.3, 0.5, 0.3] },
    celebrating: { opacity: [0.5, 1, 0.5], scale: [1, 1.3, 1] }
  };

  return (
    <div className="mira-orb-container">
      <motion.div
        className="mira-orb-glow-outer"
        style={{ background: stateConfig.glowColor }}
        variants={glowVariants}
        animate={state}
      />
      <motion.div
        className="mira-orb-glow-middle"
        style={{ background: `radial-gradient(circle, ${stateConfig.color}40 0%, transparent 70%)` }}
        animate={{
          scale: state === 'speaking' ? [1, 1.15, 1] : [1, 1.1, 1],
          opacity: state === 'idle' ? [0.2, 0.4, 0.2] : [0.4, 0.6, 0.4]
        }}
        transition={{
          duration: state === 'speaking' ? 1.2 : 2,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      <motion.div
        className="mira-orb"
        style={{
          background: `radial-gradient(circle at 40% 35%, ${stateConfig.color}ee 0%, ${stateConfig.color}bb 40%, ${stateConfig.color}88 70%, ${stateConfig.color}55 100%)`,
          boxShadow: `0 0 60px ${stateConfig.glowColor}, 0 0 30px ${stateConfig.color}60, inset 0 -20px 40px rgba(0,0,0,0.2), inset 0 10px 20px rgba(255,255,255,0.2)`,
          border: `1px solid rgba(255,255,255,0.2)`
        }}
        variants={orbVariants}
        animate={state}
      >
        <div className="mira-orb-highlight" />
        <motion.div
          className="mira-orb-sparkle"
          variants={sparkleVariants}
          animate={state}
          transition={{ duration: 1, repeat: Infinity }}
          style={{ background: `radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)` }}
        />
      </motion.div>
    </div>
  );
};

/**
 * FriendAvatars - Discord-style presence (only for logged-in users)
 */
const FriendAvatars = ({ friends = [] }) => {
  if (friends.length === 0) return null;

  return (
    <div className="friend-avatars-row">
      {friends.slice(0, 4).map((friend) => (
        <div
          key={friend.id}
          className={`friend-avatar-chip ${friend.isSpeaking ? 'speaking' : ''}`}
        >
          <div className="friend-avatar-circle">
            {friend.avatar ? (
              <img src={friend.avatar} alt={friend.name} />
            ) : (
              <span>{friend.name?.charAt(0)?.toUpperCase() || '?'}</span>
            )}
            {friend.isSpeaking && (
              <motion.div
                className="speaking-ring"
                animate={{ scale: [1, 1.2, 1], opacity: [0.8, 0.4, 0.8] }}
                transition={{ repeat: Infinity, duration: 0.8 }}
              />
            )}
          </div>
          <span className="friend-name">{friend.name?.split(' ')[0]}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * OnlineFriendsBar - Shows friends who are online but NOT in session
 *
 * UX Principle: Discord shows who's available without requiring a dropdown click
 * This removes friction - user sees at a glance who they can invite
 */
const OnlineFriendsBar = ({ friends = [], onJoinFriend }) => {
  if (friends.length === 0) return null;

  return (
    <motion.div
      className="online-friends-bar"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
    >
      <div className="online-friends-label">
        <span className="pulse-dot-small" />
        <span>Friends online</span>
      </div>
      <div className="online-friends-pills">
        {friends.slice(0, 3).map((friend) => (
          <motion.button
            key={friend.id}
            className="online-friend-pill"
            onClick={() => onJoinFriend?.(friend)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={`Join ${friend.username || friend.name}'s session`}
          >
            <span className="pill-avatar">
              {(friend.username || friend.name)?.charAt(0)?.toUpperCase() || '?'}
            </span>
            <span className="pill-name">{(friend.username || friend.name)?.split(' ')[0]}</span>
            {friend.subject && (
              <span className="pill-subject">{friend.subject.substring(0, 8)}</span>
            )}
          </motion.button>
        ))}
        {friends.length > 3 && (
          <span className="online-more-count">+{friends.length - 3}</span>
        )}
      </div>
    </motion.div>
  );
};

/**
 * ActiveSessionView - Main component
 *
 * Props:
 * - friends: Friends currently IN the session (speaking indicators)
 * - onlineFriends: Friends online but NOT in session (can be invited)
 * - onJoinFriend: Handler when user clicks to join a friend's session
 * - onInviteMore: Handler to invite additional friends (opens StudyCodeSheet)
 * - isClutchTime: When true, orb uses amber/orange color scheme
 * - miraOrbColor: Custom orb color (default, coral, ocean, sunset, midnight, forest, lavender)
 * - miraGlowEffect: Custom glow effect (default, pulse, sparkle, aurora, rainbow)
 * - miraEyeStyle: Custom eye style (default, starry, happy, sleepy, confident)
 * - curiosityMomentum: (2026) Continuous 0-1 value affecting orb physics/aliveness
 */
const ActiveSessionView = ({
  isConnected = false,
  isConnecting = false,
  agentState = 'idle',
  friends = [],
  onlineFriends = [],    // Friends available to invite (visible without dropdown)
  onJoinFriend,          // Handler to join friend's session
  onInviteMore,          // Handler to invite more friends (2025 FaceTime style)
  user = null,           // Pass user to show social features only when logged in
  isClutchTime = false,  // Clutch Time mode - amber/orange orb
  // Silent Mode - text-only for classroom use
  isSilentMode = false,
  onToggleSilentMode,
  // Mira customization (from token shop)
  miraOrbColor = 'default',
  miraGlowEffect = 'default',
  miraEyeStyle = 'default',
  // 2026 Curiosity Ambient Feedback - affects orb "aliveness"
  curiosityMomentum = 0.5,
  className = ''
}) => {
  const miraState = isConnected ? agentState : 'idle';
  // Use custom color states if customized, otherwise default/clutch time
  const states = isClutchTime
    ? CLUTCH_TIME_STATES
    : (miraOrbColor !== 'default' ? getMiraStateColors(miraOrbColor) : MIRA_STATES);
  const statusLabel = states[miraState]?.label || 'Mira';
  const isLoggedIn = Boolean(user && user.id);

  return (
    <div className={`active-session-view ${isClutchTime ? 'clutch-time-mode' : ''} ${className}`}>
      {/* Mira orb - centered */}
      <div className="session-main">
        <MiraOrb
          state={miraState}
          isClutchTime={isClutchTime}
          miraOrbColor={miraOrbColor}
          miraGlowEffect={miraGlowEffect}
          curiosityMomentum={curiosityMomentum}
        />

        {/* Silent Mode badge */}
        {isSilentMode && (
          <motion.div
            className="silent-mode-badge"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
              <line x1="2" x2="22" y1="2" y2="22"/>
            </svg>
            <span>Silent Mode</span>
            {onToggleSilentMode && (
              <button className="silent-mode-exit" onClick={onToggleSilentMode}>
                Exit
              </button>
            )}
          </motion.div>
        )}

        {/* Status line */}
        <motion.div
          className={`session-status ${isClutchTime ? 'clutch-time' : ''} ${isSilentMode ? 'silent-mode' : ''}`}
          key={statusLabel}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {isClutchTime && <span className="clutch-time-icon">⚡</span>}
          {isConnecting ? 'Connecting...' : (isSilentMode ? 'Type your question below' : statusLabel)}
        </motion.div>

        {/* Friends in session - only for voice mode (invite/join requires LiveKit room) */}
        {isLoggedIn && !isSilentMode && friends.length > 0 && (
          <FriendAvatars friends={friends} />
        )}

        {/* Invite More button - voice-only (requires LiveKit room for invite token) */}
        {isLoggedIn && !isSilentMode && isConnected && onInviteMore && (
          <motion.button
            className="invite-more-pill"
            onClick={onInviteMore}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <span className="invite-more-icon">+</span>
            <span>Invite {friends.length > 0 ? 'More' : 'Friends'}</span>
            {friends.length > 0 && friends.length < 4 && (
              <span className="invite-more-count">{4 - friends.length} slots</span>
            )}
          </motion.button>
        )}

        {/* Online friends available to invite - voice-only (join requires LiveKit room) */}
        {isLoggedIn && !isSilentMode && onlineFriends.length > 0 && (
          <OnlineFriendsBar friends={onlineFriends} onJoinFriend={onJoinFriend} />
        )}
      </div>

      <style>{`
        .active-session-view {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          min-height: 300px;
        }

        .session-main {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        /* Mira Orb */
        .mira-orb-container {
          position: relative;
          width: 200px;
          height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mira-orb-glow-outer {
          position: absolute;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          filter: blur(50px);
        }

        .mira-orb-glow-middle {
          position: absolute;
          width: 140%;
          height: 140%;
          border-radius: 50%;
          filter: blur(30px);
        }

        .mira-orb {
          position: relative;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mira-orb-highlight {
          position: absolute;
          top: 15%;
          left: 20%;
          width: 30%;
          height: 30%;
          background: radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(8px);
        }

        .mira-orb-sparkle {
          position: absolute;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          top: 25%;
          left: 30%;
          filter: blur(2px);
        }

        /* Status */
        .session-status {
          margin-top: 24px;
          font-size: 18px;
          font-weight: 600;
          color: #297A92;
          text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .session-status.clutch-time {
          color: #D97706;
        }

        .session-status.silent-mode {
          color: #6B7280;
        }

        /* Silent Mode badge */
        .silent-mode-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(107, 114, 128, 0.1);
          border: 1px solid rgba(107, 114, 128, 0.2);
          border-radius: 20px;
          color: #6B7280;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 12px;
        }

        .silent-mode-exit {
          background: rgba(107, 114, 128, 0.15);
          border: none;
          border-radius: 12px;
          padding: 4px 10px;
          font-size: 12px;
          font-weight: 500;
          color: #6B7280;
          cursor: pointer;
          margin-left: 4px;
          transition: all 0.15s ease;
        }

        .silent-mode-exit:hover {
          background: rgba(107, 114, 128, 0.25);
          color: #374151;
        }

        .clutch-time-icon {
          font-size: 16px;
        }

        /* Friends row - Discord style */
        .friend-avatars-row {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-top: 24px;
        }

        .friend-avatar-chip {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .friend-avatar-circle {
          position: relative;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #297A92, #6DB4CD);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 600;
          color: white;
          overflow: visible;
        }

        .friend-avatar-circle img {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
        }

        .speaking-ring {
          position: absolute;
          inset: -3px;
          border: 2px solid #2DD4BF;
          border-radius: 50%;
          pointer-events: none;
        }

        .friend-name {
          font-size: 11px;
          color: #297A92;
          font-weight: 500;
        }

        /* Online Friends Bar - Discord-style "see who's available" */
        .online-friends-bar {
          margin-top: 28px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          border: 1px solid rgba(41, 122, 146, 0.15);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .online-friends-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 500;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .pulse-dot-small {
          width: 6px;
          height: 6px;
          background: #10b981;
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.2); }
        }

        .online-friends-pills {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 8px;
        }

        .online-friend-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px 6px 6px;
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          border: 1px solid #e2e8f0;
          border-radius: 100px;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .online-friend-pill:hover {
          background: linear-gradient(135deg, #297A92, #3d9bb0);
          border-color: #297A92;
        }

        .online-friend-pill:hover .pill-name,
        .online-friend-pill:hover .pill-subject {
          color: white;
        }

        .pill-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #297A92, #6DB4CD);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          color: white;
        }

        .pill-name {
          font-size: 13px;
          font-weight: 500;
          color: #334155;
          transition: color 0.15s ease;
        }

        .pill-subject {
          font-size: 11px;
          color: #94a3b8;
          padding: 2px 6px;
          background: rgba(0, 0, 0, 0.05);
          border-radius: 4px;
          transition: color 0.15s ease, background 0.15s ease;
        }

        .online-friend-pill:hover .pill-subject {
          background: rgba(255, 255, 255, 0.2);
        }

        .online-more-count {
          font-size: 12px;
          font-weight: 500;
          color: #64748b;
          padding: 6px 10px;
          background: #f1f5f9;
          border-radius: 100px;
        }

        /* Invite More Pill - 2025 FaceTime/Discord style */
        .invite-more-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 20px;
          padding: 10px 18px;
          background: linear-gradient(135deg, #10b981, #06b6d4);
          border: none;
          border-radius: 100px;
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3), 0 2px 8px rgba(6, 182, 212, 0.2);
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .invite-more-pill:hover {
          box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4), 0 4px 12px rgba(6, 182, 212, 0.3);
        }

        .invite-more-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: rgba(255, 255, 255, 0.25);
          border-radius: 50%;
          font-size: 16px;
          font-weight: 700;
          line-height: 1;
        }

        .invite-more-count {
          font-size: 11px;
          font-weight: 500;
          padding: 3px 8px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 100px;
          opacity: 0.9;
        }

        /* Responsive */
        @media (max-width: 380px) {
          .mira-orb-container {
            width: 160px;
            height: 160px;
          }

          .mira-orb {
            width: 100px;
            height: 100px;
          }

          .friend-avatar-circle {
            width: 36px;
            height: 36px;
            font-size: 14px;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .mira-orb,
          .mira-orb-glow-outer,
          .mira-orb-glow-middle,
          .speaking-ring {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ActiveSessionView;
